import {default as React, useState, useEffect} from 'react';
import { User } from "oidc-client";
import { Row, Col } from 'reactstrap';
import BlockUi from 'react-block-ui'; 
import CandidateType from '../../model/hros/recruiting/CandidateType';
import {
  IContactInformationForm,
  IEducationInstanceForm, 
  ILicensureInstanceForm, 
  ICertificationInstanceForm, 
  IEmploymentHistoryInstanceForm, 
  IInstanceForm,
  IValidatedForm, 
  IFormValidationEvent
} from '../../model/forms';
import { 
  ContactInformation, 
  EducationIsEmpty, EducationInstancedSection,
  LicensureIsEmpty, LicensureInstancedSection,
  CertificationIsEmpty, CertificationInstancedSection,
  EmploymentHistoryIsEmpty, EmploymentHistoryInstancedSection
} from '../profile';
import { IInstancedSectionFieldEvent } from '../profile/InstancedSectionBase';
import { IFieldEvent } from '../fields/interfaces';
import { HROSToProfileFormMapper, ProfileFormToHROSMapper } from '../../utils/Mapping';
import { AsyncStatus, AsyncStatusEnum } from '../../model/common/AsyncStatus';
import Button from '../ui/Button';
import Loading from '../Loading';

import 'react-block-ui/style.css';
import 'react-toastify/dist/ReactToastify.css';
import toastHelper from '../../utils/toastHelper';

const FORM_FIELD_NAME_DELIMITER = "_";
enum SectionType {
  CONTACT_INFORMATION = "contactInformation",
  EDUCATION = "educations",
  LICENSURE = "licensures",
  CERTIFICATION = "certifications",
  EMPLOYMENT_HISTORY = "employmentHistories"
}

interface IProfileFormProperties {
  profile?: CandidateType;
  user: User | null;
  saveStatus?: AsyncStatus;
  onSaveProfile?: (profile: CandidateType, onSuccess: () => void) => void;
}

const contactInformationInitialState: IValidatedForm<IContactInformationForm> = { form: {}, touched: false };
const educationsInitialState: IValidatedForm<Map<number, IEducationInstanceForm>> = { form: new Map<number, IEducationInstanceForm>(), touched: false };
const licensuresInitialState: IValidatedForm<Map<number, ILicensureInstanceForm>> = { form: new Map<number, ILicensureInstanceForm>(), touched: false };
const certificationsInitialState: IValidatedForm<Map<number, ICertificationInstanceForm>> = { form: new Map<number, ICertificationInstanceForm>(), touched: false };
const employmentHistoriesInitialState: IValidatedForm<Map<number, IEmploymentHistoryInstanceForm>> = { form: new Map<number, IEmploymentHistoryInstanceForm>(), touched: false };

export default function ProfileForm({
  profile,
  user,
  saveStatus,
  onSaveProfile
} : IProfileFormProperties) {
  const profileForm = HROSToProfileFormMapper(profile);
  
  const [contactInformation, setContactInformation] = useState(() : IValidatedForm<IContactInformationForm> => {
    if (profileForm === undefined) {
      return contactInformationInitialState;
    }
    
    let initState = profileForm.contactInformation;

    if (user !== null ) {
      initState.givenName = user.profile.given_name;
      initState.familyName = user.profile.family_name;
      initState.email = user.profile.preferred_username;
    }

    return { form: initState, touched: false };
  });

  const [educations, setEducations] = useState(() : IValidatedForm<Map<number, IEducationInstanceForm>> => {
    if (profileForm === undefined) {
      return educationsInitialState;
    }
    
    return { form: profileForm.educations, touched: false };
  });

  const [licensures, setLicensures] = useState(() : IValidatedForm<Map<number, ILicensureInstanceForm>> => {
    if (profileForm === undefined) {
      return licensuresInitialState;
    }
    
    return { form: profileForm.licensures, touched: false };
  });

  const [certifications, setCertifications] = useState(() : IValidatedForm<Map<number, ICertificationInstanceForm>> => {
    if (profileForm === undefined) {
      return certificationsInitialState;
    }
    
    return { form: profileForm.certifications, touched: false };
  });

  const [employmentHistories, setEmploymentHistories] = useState(() : IValidatedForm<Map<number, IEmploymentHistoryInstanceForm>> => {
    if (profileForm === undefined) {
      return employmentHistoriesInitialState;
    }
    
    return { form: profileForm.employmentHistories, touched: false };
  });

  const [formTouched, setFormTouched] = useState(false);

  useEffect(() => {
    //Something in form state has changed
    if (
      contactInformation.touched ||
      educations.touched ||
      licensures.touched ||
      certifications.touched ||
      employmentHistories.touched
    ) {
      setFormTouched(true);
    }
  },
  [contactInformation, educations, licensures, certifications, employmentHistories]);

  const onFieldChange = function(type: SectionType, event: IFieldEvent | IInstancedSectionFieldEvent) {
    switch(type) {
      case SectionType.EDUCATION:
        onInstanceFieldChange<IEducationInstanceForm>(setEducations, event as IInstancedSectionFieldEvent);
        break;
      case SectionType.LICENSURE:
        onInstanceFieldChange<ILicensureInstanceForm>(setLicensures, event as IInstancedSectionFieldEvent);
        break;
      case SectionType.CERTIFICATION:
        onInstanceFieldChange<ICertificationInstanceForm>(setCertifications, event as IInstancedSectionFieldEvent);
        break;
        case SectionType.EMPLOYMENT_HISTORY:
          onInstanceFieldChange<IEmploymentHistoryInstanceForm>(setEmploymentHistories, event as IInstancedSectionFieldEvent);
          break;
      case SectionType.CONTACT_INFORMATION:
      default:
        onContactInformationFieldChange(event as IFieldEvent);
        break;
    }
  };

  const onAddInstance = function<T extends IInstanceForm>(stateFunc: React.Dispatch<React.SetStateAction<IValidatedForm<Map<number, T>>>>) {
    stateFunc((prevState: IValidatedForm<Map<number, T>>) => {
      let newMap = new Map<number, T>(prevState.form);
      let newId = newMap.size + 1;
      newMap.set(newId, { instanceId: newId } as T);

      return {
        validation: prevState.validation,
        form: newMap,
        touched: prevState.touched //no change
      };
    });
  };

  const onRemoveInstance = function<T extends IInstanceForm>(stateFunc: React.Dispatch<React.SetStateAction<IValidatedForm<Map<number, T>>>>, instanceId: number) {
    stateFunc((prevState: IValidatedForm<Map<number, T>>) => {
      let newMap = new Map<number, T>();
      prevState.form.delete(instanceId);

      let errors: Map<string, string> = new Map<string, string>();
      let oldErrors: Map<string, string> = prevState.validation?.errors || new Map<string, string>();

      let newInstanceId = 0;
      prevState.form.forEach((value: T) => {
        let oldInstanceId = value.instanceId;
        value.instanceId = ++newInstanceId;
        newMap.set(value.instanceId, value);
        oldErrors.forEach((errorValue: string, errorKey: string) => {
          if (errorKey.endsWith(`$${oldInstanceId}`)) {
            let newErrorKey = `${errorKey.substring(0, errorKey.length - 2)}$${value.instanceId}`;
            errors.set(newErrorKey, errorValue);
          }
        });
      });

      var newValidation = {
        valid: errors.size === 0,
        errors: errors
      }

      return { 
        validation: newValidation,
        form: newMap,
        touched: true
      };
    });
  }

  const onContactInformationFieldChange = function(event: IFieldEvent) {
    let parts = event.name.split(FORM_FIELD_NAME_DELIMITER);
    let target = parts[parts.length - 1];

    setContactInformation((prevState) => {
      let form: any = {
        ...prevState.form,
        [target]: event.value
      };

      if (event.value.trim().length === 0) {
        delete form[target];
      }

      return {
        form: form,
        validation: prevState.validation,
        touched: true
      }
    });
  };

  const onInstanceFieldChange = function<T extends IInstanceForm>(stateFunc: React.Dispatch<React.SetStateAction<IValidatedForm<Map<number, T>>>>, event: IInstancedSectionFieldEvent) {
    let parts = event.name.split(FORM_FIELD_NAME_DELIMITER);
    let target = parts[parts.length - 1];

    stateFunc((prevState: IValidatedForm<Map<number, T>>) => {
      let mapToChange: T | undefined = prevState.form.get(event.instanceId);
      let newMap = new Map<number, T>(prevState.form);

      if (mapToChange !== undefined) {
        let newInstance = {
          ...mapToChange,
          [target]: event.value
        };

        if (event.value.trim().length === 0) {
          delete newInstance[target];
        }

        newMap.set(newInstance.instanceId, newInstance);
      }

      return { 
        form: newMap,
        validation: prevState.validation,
        touched: true
      };
    });
  };

  const onValidate = function<T>(stateFunc: React.Dispatch<React.SetStateAction<IValidatedForm<T>>>, event: IFormValidationEvent) {
    stateFunc((prevState: IValidatedForm<T>) => {
      let parts = event.name.split(FORM_FIELD_NAME_DELIMITER);
      let target = parts[parts.length - 1];

      let errors: Map<string, string>;

      if (prevState.validation?.errors !== undefined) {
        errors = new Map<string, string>(prevState.validation.errors);
      }
      else
      {
        errors = new Map<string, string>();
      }

      if (event.result === true) {
        errors.delete(target);
      }
      else if (event.message !== undefined)
      {
        errors.set(target, event.message);
      }
      return {
        form: prevState.form,
        validation: {
          valid: errors.size === 0,
          errors: errors
        }
      } as IValidatedForm<T>;
    });
  }

  const onSubmitSaveProfile = function() {
    if  (
      formTouched === false ||
      contactInformation.validation?.valid === false ||
      educations.validation?.valid === false || 
      certifications.validation?.valid === false ||
      licensures.validation?.valid === false ||
      employmentHistories.validation?.valid === false
    ) {
      toastHelper.error("Validation errors need to be addressed before the profile can be saved.");
      return;
    }

    let hrosProfile = ProfileFormToHROSMapper({
      contactInformation: contactInformation.form,
      educations: educations.form,
      certifications: certifications.form,
      licensures: licensures.form,
      employmentHistories: employmentHistories.form
    });

    if (onSaveProfile !== undefined) {
      onSaveProfile(hrosProfile, () => {
        setContactInformation((prev) => { return { ...prev, touched: false }; });
        setEducations((prev) => { return { ...prev, touched: false } });
        setCertifications((prev) => { return { ...prev, touched: false } });
        setLicensures((prev) => { return { ...prev, touched: false } });
        setEmploymentHistories((prev) => { return { ...prev, touched: false } });
        setFormTouched(false);
      });
    }
    return;
  };
  
  return <React.Fragment>
    <BlockUi tag="div" blocking={saveStatus === AsyncStatusEnum.LOADING}>
      <ContactInformation 
        data={contactInformation.form}
        onFieldChanged={onFieldChange.bind(undefined, SectionType.CONTACT_INFORMATION)} 
        onValidate={(event: IFormValidationEvent) => { onValidate<IContactInformationForm>(setContactInformation, event); }}
        validation={contactInformation.validation} />
      <EducationInstancedSection
        label="Education"
        maxNumberOfSections={5}
        instances={educations.form}
        noSectionText="You have not added any education histories yet, please use the plus sign above to add one."
        onAddInstance={onAddInstance.bind(undefined, setEducations)}
        onRemoveInstance={onRemoveInstance.bind(undefined, setEducations)}
        onFieldChange={onFieldChange.bind(undefined, SectionType.EDUCATION)}
        onValidate={(event: IFormValidationEvent) => { onValidate<Map<number, IEducationInstanceForm>>(setEducations, event); }}
        validation={educations.validation}
        isEmpty={EducationIsEmpty} />
      <LicensureInstancedSection
        label="Licensure"
        maxNumberOfSections={5}
        instances={licensures.form}
        noSectionText="You have not added any licensures yet, please use the plus sign above to add one."
        onAddInstance={onAddInstance.bind(undefined, setLicensures)}
        onRemoveInstance={onRemoveInstance.bind(undefined, setLicensures)}
        onFieldChange={onFieldChange.bind(undefined, SectionType.LICENSURE)}
        onValidate={(event: IFormValidationEvent) => { onValidate<Map<number, ILicensureInstanceForm>>(setLicensures, event); }}
        validation={licensures.validation}
        isEmpty={LicensureIsEmpty} />
      <CertificationInstancedSection
        label="Certification"
        maxNumberOfSections={5}
        instances={certifications.form}
        noSectionText="You have not added any certifications yet, please use the plus sign above to add one."
        onAddInstance={onAddInstance.bind(undefined, setCertifications)}
        onRemoveInstance={onRemoveInstance.bind(undefined, setCertifications)}
        onFieldChange={onFieldChange.bind(undefined, SectionType.CERTIFICATION)}
        onValidate={(event: IFormValidationEvent) => { onValidate<Map<number, ICertificationInstanceForm>>(setCertifications, event); }}
        validation={certifications.validation}
        isEmpty={CertificationIsEmpty} />
      <EmploymentHistoryInstancedSection
        label="Employment History"
        maxNumberOfSections={5}
        instances={employmentHistories.form}
        noSectionText="You have not added any employment histories yet, please use the plus sign above to add one."        
        onAddInstance={onAddInstance.bind(undefined, setEmploymentHistories)}
        onRemoveInstance={onRemoveInstance.bind(undefined, setEmploymentHistories)}
        onFieldChange={onFieldChange.bind(undefined, SectionType.EMPLOYMENT_HISTORY)}
        onValidate={(event: IFormValidationEvent) => { onValidate<Map<number, IEmploymentHistoryInstanceForm>>(setEmploymentHistories, event); }}
        validation={employmentHistories.validation}
        isEmpty={EmploymentHistoryIsEmpty} />
    </BlockUi>
    <Row>
      <Col xs={12}>
        &nbsp;
      </Col>
    </Row>
    <Row>
      <Col xs={12} className="text-center">
        <Button isPerformingAction={saveStatus === AsyncStatusEnum.LOADING} disabled={saveStatus === AsyncStatusEnum.LOADING || formTouched === false} onClick={onSubmitSaveProfile} label="Save Profile" symbol="save" />
      </Col>
    </Row>
  </React.Fragment>;
};